import { useId } from "@fluentui/react-hooks";
import { useTranslation } from "react-i18next";
import { TextField, ITextFieldProps, Checkbox, ICheckboxProps, Dropdown, IDropdownProps, IDropdownOption, SpinButton, Stack } from "@fluentui/react";
import { HelpCallout } from "../HelpCallout";
import { VectorSettings } from "../VectorSettings";
import { RetrievalMode } from "../../api";
import { useClinicalFilters } from "../../clinicalContext";
import styles from "./Settings.module.css";

// Add type for onRenderLabel
type RenderLabelType = ITextFieldProps | IDropdownProps | ICheckboxProps;

export interface SettingsProps {
    promptTemplate: string;
    temperature: number;
    retrieveCount: number;
    agenticReasoningEffort: string;
    seed: number | null;
    minimumSearchScore: number;
    minimumRerankerScore: number;
    useSemanticRanker: boolean;
    useSemanticCaptions: boolean;
    useQueryRewriting: boolean;
    reasoningEffort: string;
    excludeCategory: string;
    includeCategory: string;
    retrievalMode: RetrievalMode;
    sendTextSources: boolean;
    sendImageSources: boolean;
    searchTextEmbeddings: boolean;
    searchImageEmbeddings: boolean;
    showSemanticRankerOption: boolean;
    showQueryRewritingOption: boolean;
    showReasoningEffortOption: boolean;
    showMultimodalOptions: boolean;
    showVectorOption: boolean;
    useLogin: boolean;
    loggedIn: boolean;
    requireAccessControl: boolean;
    className?: string;
    onChange: (field: string, value: any) => void;
    streamingEnabled?: boolean; // Only used in chat
    shouldStream?: boolean; // Only used in Chat
    useSuggestFollowupQuestions?: boolean; // Only used in Chat
    promptTemplatePrefix?: string;
    promptTemplateSuffix?: string;
    showAgenticRetrievalOption?: boolean;
    useAgenticKnowledgeBase?: boolean;
    useBudgetMonitor?: boolean;
    hideMinimalRetrievalReasoningOption?: boolean;
    useWebSource?: boolean;
    showWebSourceOption?: boolean;
    useSharePointSource?: boolean;
    showSharePointSourceOption?: boolean;
    contextPerspective?: "focused_rag" | "whole_patient_review";
    retrievalEffort?: "quick" | "balanced" | "best";
    useLangGraphClinicalRetrieval?: boolean;
    clinicalChatGraphMode?: "off" | "trace" | "supplemental" | "fusion";
    clinicalChatQueryRewriteMode?: "auto" | "deterministic" | "llm";
    clinicalChatRuntimePlannerMode?: "deterministic" | "llm";
    clinicalChatRerankerMode?: "disabled" | "lexical";
    showGraphSupplementalEvidenceOption?: boolean;
    showGraphSupplementalEvidence?: boolean;
    // New Developer Settings props
    availableModels?: { name: string; modelId?: string; maxTokens: number; isCurrent: boolean; isRecommended?: boolean; providerName?: string }[];
    selectedModel?: string;
    selectedTemplate?: string;
    onModelChange?: (model: string) => void;
    onTemplateChange?: (template: string) => void;
    qaNotesLimit?: number;
    page?: "chat" | "qa";
}

export const Settings = ({
    promptTemplate,
    temperature,
    retrieveCount,
    agenticReasoningEffort,
    seed,
    minimumSearchScore,
    minimumRerankerScore,
    useSemanticRanker,
    useSemanticCaptions,
    useQueryRewriting,
    reasoningEffort,
    excludeCategory,
    includeCategory,
    retrievalMode,
    searchTextEmbeddings,
    searchImageEmbeddings,
    sendTextSources,
    sendImageSources,
    showSemanticRankerOption,
    showQueryRewritingOption,
    showReasoningEffortOption,
    showMultimodalOptions,
    showVectorOption,
    useLogin,
    loggedIn,
    requireAccessControl,
    className,
    onChange,
    streamingEnabled,
    shouldStream,
    useSuggestFollowupQuestions,
    promptTemplatePrefix,
    promptTemplateSuffix,
    showAgenticRetrievalOption,
    useAgenticKnowledgeBase = false,
    useBudgetMonitor = true,
    hideMinimalRetrievalReasoningOption = false,
    useWebSource = false,
    showWebSourceOption = false,
    useSharePointSource = false,
    showSharePointSourceOption = false,
    contextPerspective = "focused_rag",
    retrievalEffort = "balanced",
    useLangGraphClinicalRetrieval = true,
    clinicalChatGraphMode = "off",
    clinicalChatQueryRewriteMode = "auto",
    clinicalChatRuntimePlannerMode = "deterministic",
    clinicalChatRerankerMode = "disabled",
    showGraphSupplementalEvidenceOption = false,
    showGraphSupplementalEvidence = false,
    availableModels,
    selectedModel,
    selectedTemplate,
    onModelChange,
    onTemplateChange,
    qaNotesLimit,
    page
}: SettingsProps) => {
    const isChat = page === "chat";
    const isQA = page === "qa";
    const { t } = useTranslation();
    const { filters: clinicalFilters, setFilters: setClinicalFilters } = useClinicalFilters();

    // Form field IDs
    const promptTemplateId = useId("promptTemplate");
    const promptTemplateFieldId = useId("promptTemplateField");
    const temperatureId = useId("temperature");
    const temperatureFieldId = useId("temperatureField");
    const seedId = useId("seed");
    const seedFieldId = useId("seedField");
    const agenticRetrievalId = useId("agenticRetrieval");
    const agenticRetrievalFieldId = useId("agenticRetrievalField");
    const langGraphClinicalRetrievalId = useId("langGraphClinicalRetrieval");
    const langGraphClinicalRetrievalFieldId = useId("langGraphClinicalRetrievalField");
    const clinicalChatGraphModeFieldId = useId("clinicalChatGraphModeField");
    const clinicalChatQueryRewriteModeFieldId = useId("clinicalChatQueryRewriteModeField");
    const clinicalChatRuntimePlannerModeFieldId = useId("clinicalChatRuntimePlannerModeField");
    const clinicalChatRerankerModeFieldId = useId("clinicalChatRerankerModeField");
    const graphSupplementalEvidenceId = useId("graphSupplementalEvidence");
    const graphSupplementalEvidenceFieldId = useId("graphSupplementalEvidenceField");
    const budgetMonitorId = useId("budgetMonitor");
    const budgetMonitorFieldId = useId("budgetMonitorField");
    const webSourceId = useId("webSource");
    const webSourceFieldId = useId("webSourceField");
    const sharePointSourceId = useId("sharePointSource");
    const sharePointSourceFieldId = useId("sharePointSourceField");
    const searchScoreId = useId("searchScore");
    const searchScoreFieldId = useId("searchScoreField");
    const rerankerScoreId = useId("rerankerScore");
    const rerankerScoreFieldId = useId("rerankerScoreField");
    const retrieveCountId = useId("retrieveCount");
    const retrieveCountFieldId = useId("retrieveCountField");
    const agenticReasoningEffortId = useId("agenticReasoningEffort");
    const agenticReasoningEffortFieldId = useId("agenticReasoningEffortField");
    const includeCategoryId = useId("includeCategory");
    const includeCategoryFieldId = useId("includeCategoryField");
    const excludeCategoryId = useId("excludeCategory");
    const excludeCategoryFieldId = useId("excludeCategoryField");
    const semanticRankerId = useId("semanticRanker");
    const semanticRankerFieldId = useId("semanticRankerField");
    const queryRewritingFieldId = useId("queryRewritingField");
    const reasoningEffortFieldId = useId("reasoningEffortField");
    const semanticCaptionsId = useId("semanticCaptions");
    const semanticCaptionsFieldId = useId("semanticCaptionsField");
    const shouldStreamId = useId("shouldStream");
    const shouldStreamFieldId = useId("shouldStreamField");
    const suggestFollowupQuestionsId = useId("suggestFollowupQuestions");
    const suggestFollowupQuestionsFieldId = useId("suggestFollowupQuestionsField");

    const webSourceDisablesStreamingAndFollowup = !!useWebSource;

    const retrievalReasoningOptions: IDropdownOption[] = [
        { key: "minimal", text: t("labels.agenticReasoningEffortOptions.minimal") },
        { key: "low", text: t("labels.agenticReasoningEffortOptions.low") },
        { key: "medium", text: t("labels.agenticReasoningEffortOptions.medium") }
    ];

    const renderLabel = (props: RenderLabelType | undefined, labelId: string, fieldId: string, helpText: string) => (
        <HelpCallout labelId={labelId} fieldId={fieldId} helpText={helpText} label={props?.label} />
    );

    return (
        <div className={className}>
            {streamingEnabled && (
                <>
                    <Checkbox
                        id={shouldStreamFieldId}
                        className={styles.settingsSeparator}
                        checked={webSourceDisablesStreamingAndFollowup ? false : shouldStream}
                        label={t("labels.shouldStream")}
                        onChange={(_ev, checked) => onChange("shouldStream", !!checked)}
                        aria-labelledby={shouldStreamId}
                        disabled={webSourceDisablesStreamingAndFollowup}
                        onRenderLabel={props => renderLabel(props, shouldStreamId, shouldStreamFieldId, t("helpTexts.streamChat"))}
                    />

                    <Checkbox
                        id={suggestFollowupQuestionsFieldId}
                        className={styles.settingsSeparator}
                        checked={webSourceDisablesStreamingAndFollowup ? false : useSuggestFollowupQuestions}
                        label={t("labels.useSuggestFollowupQuestions")}
                        onChange={(_ev, checked) => onChange("useSuggestFollowupQuestions", !!checked)}
                        aria-labelledby={suggestFollowupQuestionsId}
                        disabled={webSourceDisablesStreamingAndFollowup}
                        onRenderLabel={props =>
                            renderLabel(props, suggestFollowupQuestionsId, suggestFollowupQuestionsFieldId, t("helpTexts.suggestFollowupQuestions"))
                        }
                    />
                </>
            )}

            <h3 className={styles.sectionHeader}>{t("searchSettings")}</h3>

            {showAgenticRetrievalOption && (
                <Checkbox
                    id={agenticRetrievalFieldId}
                    className={styles.settingsSeparator}
                    checked={useAgenticKnowledgeBase}
                    label={t("labels.useAgenticKnowledgeBase")}
                    onChange={(_ev, checked) => onChange("useAgenticKnowledgeBase", !!checked)}
                    aria-labelledby={agenticRetrievalId}
                    onRenderLabel={props => renderLabel(props, agenticRetrievalId, agenticRetrievalFieldId, t("helpTexts.useAgenticKnowledgeBase"))}
                />
            )}

            <Checkbox
                id={budgetMonitorFieldId}
                className={styles.settingsSeparator}
                checked={useBudgetMonitor}
                label={t("labels.useBudgetMonitor")}
                onChange={(_ev, checked) => onChange("useBudgetMonitor", !!checked)}
                aria-labelledby={budgetMonitorId}
                onRenderLabel={props => renderLabel(props, budgetMonitorId, budgetMonitorFieldId, t("helpTexts.useBudgetMonitor"))}
            />

            {isChat && !useAgenticKnowledgeBase && (
                <>
                    <Checkbox
                        id={langGraphClinicalRetrievalFieldId}
                        className={styles.settingsSeparator}
                        checked={useLangGraphClinicalRetrieval}
                        label="LangGraph clinical retrieval"
                        onChange={(_ev, checked) => onChange("useLangGraphClinicalRetrieval", !!checked)}
                        aria-labelledby={langGraphClinicalRetrievalId}
                        onRenderLabel={props =>
                            renderLabel(
                                props,
                                langGraphClinicalRetrievalId,
                                langGraphClinicalRetrievalFieldId,
                                "Routes clinical note retrieval through the default LangGraph orchestration path. Disable to use the deterministic pipeline for diagnostics."
                            )
                        }
                    />
                    {showGraphSupplementalEvidenceOption && (
                        <Checkbox
                            id={graphSupplementalEvidenceFieldId}
                            className={styles.settingsSeparator}
                            checked={showGraphSupplementalEvidence}
                            label="Show graph-supported notes"
                            onChange={(_ev, checked) => onChange("showGraphSupplementalEvidence", !!checked)}
                            aria-labelledby={graphSupplementalEvidenceId}
                            onRenderLabel={props =>
                                renderLabel(
                                    props,
                                    graphSupplementalEvidenceId,
                                    graphSupplementalEvidenceFieldId,
                                    "Requests an additional collapsed section with reviewed graph-supported notes that are separate from the answer citations."
                                )
                            }
                        />
                    )}
                    <Dropdown
                        id={clinicalChatGraphModeFieldId}
                        className={styles.settingsSeparator}
                        label="Clinical graph mode"
                        selectedKey={clinicalChatGraphMode}
                        options={[
                            { key: "off", text: "Off" },
                            { key: "trace", text: "Trace only" },
                            { key: "supplemental", text: "Supplemental evidence" },
                            { key: "fusion", text: "Fusion experiment" }
                        ]}
                        onChange={(_, option) => option && onChange("clinicalChatGraphMode", option.key)}
                    />
                    <Dropdown
                        id={clinicalChatQueryRewriteModeFieldId}
                        className={styles.settingsSeparator}
                        label="Clinical query rewrite"
                        selectedKey={clinicalChatQueryRewriteMode}
                        options={[
                            { key: "auto", text: "Auto" },
                            { key: "deterministic", text: "Deterministic" },
                            { key: "llm", text: "LLM" }
                        ]}
                        onChange={(_, option) => option && onChange("clinicalChatQueryRewriteMode", option.key)}
                    />
                    <Dropdown
                        id={clinicalChatRuntimePlannerModeFieldId}
                        className={styles.settingsSeparator}
                        label="Runtime planner"
                        selectedKey={clinicalChatRuntimePlannerMode}
                        options={[
                            { key: "deterministic", text: "Deterministic" },
                            { key: "llm", text: "LLM diagnostic" }
                        ]}
                        onChange={(_, option) => option && onChange("clinicalChatRuntimePlannerMode", option.key)}
                    />
                    <Dropdown
                        id={clinicalChatRerankerModeFieldId}
                        className={styles.settingsSeparator}
                        label="Experimental reranker"
                        selectedKey={clinicalChatRerankerMode}
                        options={[
                            { key: "disabled", text: "Disabled" },
                            { key: "lexical", text: "Lexical" }
                        ]}
                        onChange={(_, option) => option && onChange("clinicalChatRerankerMode", option.key)}
                    />
                    <Dropdown
                        className={styles.settingsSeparator}
                        label="Context perspective"
                        selectedKey={contextPerspective}
                        options={[
                            { key: "focused_rag", text: "Focused RAG" },
                            { key: "whole_patient_review", text: "Whole patient review" }
                        ]}
                        onChange={(_, option) => option && onChange("contextPerspective", option.key)}
                    />
                    <Dropdown
                        className={styles.settingsSeparator}
                        label="Retrieval effort"
                        selectedKey={retrievalEffort}
                        options={[
                            { key: "quick", text: "Quick" },
                            { key: "balanced", text: "Balanced" },
                            { key: "best", text: "Best" }
                        ]}
                        onChange={(_, option) => option && onChange("retrievalEffort", option.key)}
                    />
                </>
            )}

            {showAgenticRetrievalOption && useAgenticKnowledgeBase && (
                <Dropdown
                    id={agenticReasoningEffortFieldId}
                    className={styles.settingsSeparator}
                    label={t("labels.agenticReasoningEffort")}
                    selectedKey={agenticReasoningEffort}
                    onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => {
                        const newValue = option?.key?.toString() ?? agenticReasoningEffort;
                        onChange("agenticReasoningEffort", newValue);
                        // If selecting minimal, disable and deselect web source
                        if (newValue === "minimal" && useWebSource) {
                            onChange("useWebSource", false);
                        }
                    }}
                    aria-labelledby={agenticReasoningEffortId}
                    options={retrievalReasoningOptions}
                    onRenderLabel={props => renderLabel(props, agenticReasoningEffortId, agenticReasoningEffortFieldId, t("helpTexts.agenticReasoningEffort"))}
                />
            )}

            {showAgenticRetrievalOption && useAgenticKnowledgeBase && showWebSourceOption && (
                <Checkbox
                    id={webSourceFieldId}
                    className={styles.settingsSeparator}
                    checked={useWebSource}
                    label={t("labels.useWebSource")}
                    onChange={(_ev, checked) => {
                        onChange("useWebSource", !!checked);
                        // If enabling web source, disable streaming and follow-up questions
                        if (checked) {
                            if (shouldStream) {
                                onChange("shouldStream", false);
                            }
                            if (useSuggestFollowupQuestions) {
                                onChange("useSuggestFollowupQuestions", false);
                            }
                        }
                    }}
                    aria-labelledby={webSourceId}
                    disabled={!useAgenticKnowledgeBase || agenticReasoningEffort === "minimal"}
                    onRenderLabel={props => renderLabel(props, webSourceId, webSourceFieldId, t("helpTexts.useWebSource"))}
                />
            )}
            {showAgenticRetrievalOption && useAgenticKnowledgeBase && showSharePointSourceOption && (
                <Checkbox
                    id={sharePointSourceFieldId}
                    className={styles.settingsSeparator}
                    checked={useSharePointSource}
                    label={t("labels.useSharePointSource")}
                    onChange={(_ev, checked) => onChange("useSharePointSource", !!checked)}
                    aria-labelledby={sharePointSourceId}
                    disabled={!useAgenticKnowledgeBase}
                    onRenderLabel={props => renderLabel(props, sharePointSourceId, sharePointSourceFieldId, t("helpTexts.useSharePointSource"))}
                />
            )}
            {!useAgenticKnowledgeBase && (
                <TextField
                    id={searchScoreFieldId}
                    className={styles.settingsSeparator}
                    label={t("labels.minimumSearchScore")}
                    type="number"
                    min={0}
                    step={0.01}
                    defaultValue={minimumSearchScore.toString()}
                    onChange={(_ev, val) => onChange("minimumSearchScore", parseFloat(val || "0"))}
                    aria-labelledby={searchScoreId}
                    onRenderLabel={props => renderLabel(props, searchScoreId, searchScoreFieldId, t("helpTexts.searchScore"))}
                />
            )}

            {showSemanticRankerOption && (
                <SpinButton
                    id={rerankerScoreFieldId}
                    className={styles.settingsSeparator}
                    label={t("labels.minimumRerankerScore")}
                    min={2}
                    max={4}
                    step={0.1}
                    value={minimumRerankerScore.toString()}
                    onIncrement={val => {
                        const v = Math.min(4, parseFloat(val) + 0.1);
                        onChange("minimumRerankerScore", Math.round(v * 10) / 10);
                        return String(Math.round(v * 10) / 10);
                    }}
                    onDecrement={val => {
                        const v = Math.max(2, parseFloat(val) - 0.1);
                        onChange("minimumRerankerScore", Math.round(v * 10) / 10);
                        return String(Math.round(v * 10) / 10);
                    }}
                    onValidate={val => {
                        const v = Math.max(2, Math.min(4, parseFloat(val) || 2));
                        onChange("minimumRerankerScore", Math.round(v * 10) / 10);
                        return String(Math.round(v * 10) / 10);
                    }}
                    aria-labelledby={rerankerScoreId}
                />
            )}

            {/* Removed: retrieveCount setting - now controlled by clinical panel AI Query Limit */}
            {/* Hidden: Include/Exclude category — not useful until doc library has meaningful categories
            <Dropdown
                id={includeCategoryFieldId}
                className={styles.settingsSeparator}
                label={t("labels.includeCategory")}
                selectedKey={includeCategory}
                onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) => onChange("includeCategory", option?.key || "")}
                aria-labelledby={includeCategoryId}
                options={[
                    { key: "", text: t("labels.includeCategoryOptions.all") }
                ]}
                onRenderLabel={props => renderLabel(props, includeCategoryId, includeCategoryFieldId, t("helpTexts.includeCategory"))}
            />
            <TextField
                id={excludeCategoryFieldId}
                className={styles.settingsSeparator}
                label={t("labels.excludeCategory")}
                defaultValue={excludeCategory}
                onChange={(_ev, val) => onChange("excludeCategory", val || "")}
                aria-labelledby={excludeCategoryId}
                onRenderLabel={props => renderLabel(props, excludeCategoryId, excludeCategoryFieldId, t("helpTexts.excludeCategory"))}
            />
            */}
            {showSemanticRankerOption && !useAgenticKnowledgeBase && (
                <>
                    <Checkbox
                        id={semanticRankerFieldId}
                        className={styles.settingsSeparator}
                        checked={useSemanticRanker}
                        label={t("labels.useSemanticRanker")}
                        onChange={(_ev, checked) => onChange("useSemanticRanker", !!checked)}
                        aria-labelledby={semanticRankerId}
                        onRenderLabel={props => renderLabel(props, semanticRankerId, semanticRankerFieldId, t("helpTexts.useSemanticReranker"))}
                    />

                    {/* Semantic captions checkbox removed - now controlled by Snippets radio button */}
                </>
            )}
            {showQueryRewritingOption && !useAgenticKnowledgeBase && (
                <>
                    <Checkbox
                        id={queryRewritingFieldId}
                        className={styles.settingsSeparator}
                        checked={useQueryRewriting}
                        disabled={!useSemanticRanker}
                        label={t("labels.useQueryRewriting")}
                        onChange={(_ev, checked) => onChange("useQueryRewriting", !!checked)}
                        aria-labelledby={queryRewritingFieldId}
                        onRenderLabel={props => renderLabel(props, queryRewritingFieldId, queryRewritingFieldId, t("helpTexts.useQueryRewriting"))}
                    />
                </>
            )}
            {showReasoningEffortOption && (
                <Dropdown
                    id={reasoningEffortFieldId}
                    selectedKey={reasoningEffort}
                    label={t("labels.reasoningEffort")}
                    onChange={(_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IDropdownOption) =>
                        onChange("reasoningEffort", option?.key || "")
                    }
                    aria-labelledby={reasoningEffortFieldId}
                    options={[
                        { key: "minimal", text: t("labels.reasoningEffortOptions.minimal") },
                        { key: "low", text: t("labels.reasoningEffortOptions.low") },
                        { key: "medium", text: t("labels.reasoningEffortOptions.medium") },
                        { key: "high", text: t("labels.reasoningEffortOptions.high") }
                    ]}
                    onRenderLabel={props => renderLabel(props, queryRewritingFieldId, queryRewritingFieldId, t("helpTexts.reasoningEffort"))}
                />
            )}
            {showVectorOption && !useAgenticKnowledgeBase && (
                <>
                    <VectorSettings
                        defaultRetrievalMode={retrievalMode}
                        defaultSearchTextEmbeddings={searchTextEmbeddings}
                        defaultSearchImageEmbeddings={searchImageEmbeddings}
                        showImageOptions={showMultimodalOptions}
                        updateRetrievalMode={val => onChange("retrievalMode", val)}
                        updateSearchTextEmbeddings={val => onChange("searchTextEmbeddings", val)}
                        updateSearchImageEmbeddings={val => onChange("searchImageEmbeddings", val)}
                    />
                </>
            )}

            {!useWebSource && (
                <>
                    <h3 className={styles.sectionHeader}>{t("llmSettings")}</h3>
                    {/* Model Selection — both pages */}
                    <Dropdown
                        label={isQA ? "Q&A Model" : t("developerSettings.modelSelection")}
                        selectedKey={selectedModel}
                        options={
                            availableModels?.map(m => ({
                                key: m.modelId || m.name,
                                text: `${m.name}${m.providerName ? ` via ${m.providerName}` : ""} (${(m.maxTokens / 1000).toFixed(0)}k tokens)${m.isRecommended ? " ★ Recommended" : ""}${m.isCurrent ? " - Current" : ""}`
                            })) || []
                        }
                        onChange={(_, option) => option && onModelChange?.(option.key as string)}
                    />

                    {/* Template Selection — both pages */}
                    <Dropdown
                        label={isQA ? "Q&A Template" : t("developerSettings.templateSelection")}
                        selectedKey={selectedTemplate || "clinical_general"}
                        options={[
                            { key: "clinical_general", text: "Clinical General ★ Default — Balanced clinical queries" },
                            { key: "clinical_safety", text: "Clinical Safety — Safety/crisis focus, flags risk factors" },
                            { key: "clinical_care_coordination", text: "Care Coordination — Medications, services, providers" },
                            { key: "chat_answer_question", text: "Original — Base template (no clinical specialization)" }
                        ]}
                        onChange={(_, option) => option && onTemplateChange?.(option.key as string)}
                    />

                    {/* AI Query Limit — all pages */}
                    <Dropdown
                        label="AI Query Limit"
                        selectedKey={String(clinicalFilters.topResults)}
                        options={[
                            {
                                key: "0",
                                text: isChat
                                    ? contextPerspective === "whole_patient_review"
                                        ? "All (whole-patient review)"
                                        : "Auto (adaptive)"
                                    : "All (Default)"
                            },
                            { key: "3", text: "3" },
                            { key: "5", text: "5" },
                            { key: "10", text: "10" },
                            { key: "25", text: "25" },
                            { key: "50", text: "50" },
                            { key: "100", text: "100" },
                            { key: "200", text: "200" }
                        ]}
                        onChange={(_, option) => option && setClinicalFilters({ ...clinicalFilters, topResults: Number(option.key) })}
                    />

                    {/* Q&A Notes Limit — Q&A only */}
                    {isQA && (
                        <Dropdown
                            label="Q&A Notes Limit"
                            selectedKey={qaNotesLimit}
                            options={[
                                { key: 10, text: "10" },
                                { key: 25, text: "25" },
                                { key: 50, text: "50 (Default)" },
                                { key: 100, text: "100" },
                                { key: 200, text: "200" },
                                { key: 500, text: "500" }
                            ]}
                            onChange={(_, option) => option && onChange("qaNotesLimit", option.key as number)}
                        />
                    )}

                    <TextField
                        id={promptTemplateFieldId}
                        className={styles.settingsSeparator}
                        defaultValue={promptTemplate}
                        label={t("labels.promptTemplate")}
                        multiline
                        autoAdjustHeight
                        onChange={(_ev, val) => onChange("promptTemplate", val || "")}
                        aria-labelledby={promptTemplateId}
                        onRenderLabel={props => renderLabel(props, promptTemplateId, promptTemplateFieldId, t("helpTexts.promptTemplate"))}
                    />
                    <SpinButton
                        id={temperatureFieldId}
                        className={styles.settingsSeparator}
                        label={t("labels.temperature")}
                        min={0}
                        max={0.3}
                        step={0.1}
                        value={temperature.toString()}
                        onIncrement={val => {
                            const v = Math.min(0.3, parseFloat(val) + 0.1);
                            onChange("temperature", Math.round(v * 10) / 10);
                            return String(Math.round(v * 10) / 10);
                        }}
                        onDecrement={val => {
                            const v = Math.max(0, parseFloat(val) - 0.1);
                            onChange("temperature", Math.round(v * 10) / 10);
                            return String(Math.round(v * 10) / 10);
                        }}
                        onValidate={val => {
                            const v = Math.max(0, Math.min(0.3, parseFloat(val) || 0));
                            onChange("temperature", Math.round(v * 10) / 10);
                            return String(Math.round(v * 10) / 10);
                        }}
                        aria-labelledby={temperatureId}
                    />
                    <Checkbox
                        id={seedFieldId}
                        className={styles.settingsSeparator}
                        label={t("labels.seed")}
                        checked={seed !== null}
                        onChange={(_ev, checked) => onChange("seed", checked ? 42 : null)}
                        onRenderLabel={props => renderLabel(props as any, seedId, seedFieldId, t("helpTexts.seed"))}
                    />

                    {showMultimodalOptions && !useAgenticKnowledgeBase && (
                        <fieldset className={styles.fieldset + " " + styles.settingsSeparator}>
                            <legend className={styles.legend}>{t("labels.llmInputs")}</legend>
                            <Stack tokens={{ childrenGap: 8 }}>
                                <Checkbox
                                    id="sendTextSources"
                                    label={t("labels.llmInputsOptions.texts")}
                                    checked={sendTextSources}
                                    onChange={(_ev, checked) => {
                                        onChange("sendTextSources", !!checked);
                                    }}
                                    onRenderLabel={props => renderLabel(props, "sendTextSourcesLabel", "sendTextSources", t("helpTexts.llmTextInputs"))}
                                />
                                <Checkbox
                                    id="sendImageSources"
                                    label={t("labels.llmInputsOptions.images")}
                                    checked={sendImageSources}
                                    onChange={(_ev, checked) => {
                                        onChange("sendImageSources", !!checked);
                                    }}
                                    onRenderLabel={props => renderLabel(props, "sendImageSourcesLabel", "sendImageSources", t("helpTexts.llmImageInputs"))}
                                />
                            </Stack>
                        </fieldset>
                    )}
                </>
            )}
        </div>
    );
};
